import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { SEOHead } from '../../components/SEOHead';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Calendar, ArrowRight, Building2 } from 'lucide-react';

interface BlogSummary {
  id: string;
  title: string;
  intro: string;
  featured_image_url: string | null;
  published_at: string;
  slug: string;
  view_count: number;
}

interface UserData {
  first_name: string;
  company_name: string;
  username_slug: string;
  user_profile: string;
  website_url: string | null;
}

interface BlogSettings {
  logo_url: string | null;
  about_text: string;
  accent_color: string;
}

export function UserBlogList() {
  const { username } = useParams<{ username: string }>();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<UserData | null>(null);
  const [blogSettings, setBlogSettings] = useState<BlogSettings | null>(null);
  const [blogs, setBlogs] = useState<BlogSummary[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadUserBlogs();
  }, [username]);

  const loadUserBlogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('first_name, company_name, username_slug, user_profile, website_url')
        .eq('username_slug', username)
        .maybeSingle();

      if (userError || !userData) {
        setError('Gebruiker niet gevonden');
        setLoading(false);
        return;
      }

      setUser(userData);

      const { data: userIdData } = await supabase
        .from('users')
        .select('id')
        .eq('username_slug', username)
        .single();

      if (!userIdData) {
        setError('Gebruiker niet gevonden');
        setLoading(false);
        return;
      }

      const { data: settingsData } = await supabase
        .from('user_blog_settings')
        .select('logo_url, about_text, accent_color')
        .eq('user_id', userIdData.id)
        .maybeSingle();

      if (settingsData) {
        setBlogSettings(settingsData);
      }

      const { data: blogsData, error: blogsError } = await supabase
        .from('published_blogs')
        .select('id, title, intro, featured_image_url, published_at, slug, view_count')
        .eq('user_id', userIdData.id)
        .eq('status', 'published')
        .eq('is_active', true)
        .order('published_at', { ascending: false })
        .limit(6);

      if (blogsError) {
        console.error('Error loading blogs:', blogsError);
        setError('Kon blogs niet laden');
        setLoading(false);
        return;
      }

      setBlogs(blogsData || []);
      setLoading(false);
    } catch (err) {
      console.error('Error loading user blogs:', err);
      setError('Er ging iets mis bij het laden');
      setLoading(false);
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  const estimateReadTime = (content: string) => {
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return minutes;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2EF] flex items-center justify-center">
        <LoadingSpinner text="Blogs laden..." />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen bg-[#F2F2EF] flex items-center justify-center p-4">
        <div className="card max-w-md text-center">
          <h1 className="text-2xl font-bold text-[#231F20] mb-4">
            {error || 'Gebruiker niet gevonden'}
          </h1>
          <a href="https://www.dibbi.nl" className="btn-secondary">
            Naar dibbi.nl
          </a>
        </div>
      </div>
    );
  }

  const pageTitle = `${user.company_name} - Blog`;
  const pageDescription = user.user_profile || `Lees de blogs van ${user.company_name} op lees-nu.nl`;
  const pageUrl = `https://lees-nu.nl/blog/${username}`;

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        url={pageUrl}
        type="website"
      />

      <div className="min-h-screen bg-[#F2F2EF]" style={{ '--accent-color': blogSettings?.accent_color || '#FF6B35' } as React.CSSProperties}>
        <header className="bg-white border-b border-gray-200 py-12">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex flex-col items-center">
              {blogSettings?.logo_url ? (
                <img
                  src={blogSettings.logo_url}
                  alt={`${user.company_name} logo`}
                  className="max-h-[120px] max-w-[180px] w-auto h-auto object-contain mb-8"
                />
              ) : (
                <Building2 className="w-16 h-16 md:w-20 md:h-20 mb-8" style={{ color: blogSettings?.accent_color || '#FF6B35' }} />
              )}

              <div className="w-full max-w-2xl">
                {blogSettings?.about_text && (
                  <div
                    className="text-lg text-gray-700 leading-relaxed prose prose-lg max-w-none text-center"
                    dangerouslySetInnerHTML={{ __html: blogSettings.about_text }}
                  />
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 py-12">
          {blogs.length === 0 ? (
            <div className="card text-center py-12">
              <p className="text-xl text-gray-600 mb-4">
                Er zijn nog geen blogs gepubliceerd
              </p>
              <p className="text-gray-500">
                Kom later terug voor nieuwe artikelen
              </p>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-[#231F20] mb-8">
                Recente blogartikelen
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {blogs.map((blog) => (
                  <Link
                    key={blog.id}
                    to={`/blog/${username}/${blog.slug}`}
                    className="card group hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                  >
                    {blog.featured_image_url && (
                      <img
                        src={blog.featured_image_url}
                        alt={blog.title}
                        className="w-full h-48 object-cover rounded-lg mb-4 group-hover:opacity-90 transition-opacity"
                      />
                    )}

                    <h3
                      className="text-xl font-bold text-[#231F20] mb-3 transition-colors line-clamp-2 group-hover:opacity-90"
                      style={{ color: '#231F20' }}
                      onMouseEnter={(e) => e.currentTarget.style.color = blogSettings?.accent_color || '#FF6B35'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#231F20'}
                    >
                      {blog.title}
                    </h3>

                    <p className="text-gray-600 mb-4 line-clamp-3 leading-relaxed">
                      {truncateText(blog.intro, 150)}
                    </p>

                    <div className="flex items-center justify-end pt-4 border-t border-gray-200">
                      <span
                        className="font-medium group-hover:gap-2 inline-flex items-center transition-all"
                        style={{ color: blogSettings?.accent_color || '#FF6B35' }}
                      >
                        Lees meer
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </main>

        <footer className="bg-white py-8 mt-12 border-t border-gray-200">
          <div className="max-w-6xl mx-auto px-4 text-center text-sm text-gray-600">
            <p>Mogelijk gemaakt door <a href="https://www.dibbi.nl" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: blogSettings?.accent_color || '#FF6B35' }}>dibbi</a></p>
          </div>
        </footer>
      </div>
    </>
  );
}
